"use client";

import { KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronDown, CircleMinus, Search, X } from "lucide-react";
import { OptionPair } from "@erp/shared-libs/common";
import { useIsMobile } from "@erp/shared-libs/hooks";
import { cn } from "@erp/shared-libs/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  filterSelectMenuOptions,
  findNextEnabledIndex,
  isSelectMenuOptionDisabled,
  orderSelectedFirst,
  SELECT_MENU_SEARCH_THRESHOLD,
  splitLabelByKeyword,
  toggleSelectMenuExclusion,
  toggleSelectMenuInclusion,
} from "./select-menu-util";

/**
 * 桌面版每一列的高度（px）
 */
const DESKTOP_ROW_HEIGHT = 36;

/**
 * 手機版每一列的高度（px）；手指好點的最小高度
 */
const MOBILE_ROW_HEIGHT = 48;

/**
 * 桌面版清單最多顯示的高度（px），超過就在清單內捲動
 */
const DESKTOP_LIST_MAX_HEIGHT = 288;

/**
 * 清單上下的內距總和（px）；算桌面版清單高度時要加回去
 */
const LIST_VERTICAL_PADDING = 8;

interface SelectMenuBaseProps {
  /**
   * 選項
   */
  options: OptionPair[];
  /**
   * 觸發鈕左邊的圖示
   */
  icon?: ReactNode;
  /**
   * 選單名稱：觸發鈕上的文字，也是手機抽屜的標題
   */
  label: string;
  /**
   * 選項還在載入（觸發鈕顯示載入中、不能點）
   */
  isLoading?: boolean;
  /**
   * 停用：觸發鈕不能點、選單打不開（和 `isLoading` 不同，不顯示載入中）
   */
  disabled?: boolean;
  /**
   * 搜尋框的提示文字；沒有傳就用「搜尋」
   */
  searchPlaceholder?: string;
}

interface SelectMenuSingleProps<T extends string | number> extends SelectMenuBaseProps {
  multiple?: false;
  /**
   * 目前選的值；沒有選是 undefined
   */
  value: T | undefined;
  values?: never;
  /**
   * 可不可以清空（預設可以）。可以清空時再點一次選中的選項會取消；
   * 不可以清空時觸發鈕直接顯示選中的選項名稱
   */
  clearable?: boolean;
  onChange: (value: T | undefined) => void;
  /**
   * 被排除的值；有傳 `onExcludedChange` 才有作用
   */
  excludedValues?: T[];
  /**
   * 有傳才開啟「排除」：每一列出現排除鈕。排除可以多個，和選中的值不並存；
   * 不可以清空的單選不提供排除
   */
  onExcludedChange?: (values: T[]) => void;
}

interface SelectMenuMultipleProps<T extends string | number> extends SelectMenuBaseProps {
  multiple: true;
  /**
   * 目前選的值
   */
  values: T[];
  value?: never;
  clearable?: never;
  onChange: (values: T[]) => void;
  /**
   * 被排除的值；有傳 `onExcludedChange` 才有作用
   */
  excludedValues?: T[];
  /**
   * 有傳才開啟「排除」：每一列出現排除鈕
   */
  onExcludedChange?: (values: T[]) => void;
}

export type SelectMenuProps<T extends string | number = string | number> =
  | SelectMenuSingleProps<T>
  | SelectMenuMultipleProps<T>;

/**
 * 可搜尋的選單，支援單選與多選。
 *
 * - 桌面是浮層，手機是從底部滑上來的抽屜，兩邊是同一份清單
 * - 多選點了不會關，選中的那一列打勾；單選點了就選定並關閉
 * - 選項超過 `SELECT_MENU_SEARCH_THRESHOLD` 個才出現搜尋框
 * - 清單只畫出看得到的那幾列，選項很多也不會卡
 * - 多選打開的當下把已選的排到最上面，勾選過程中順序不變；單選照原本的順序（選項的順序常有意義，
 *   例：天數、排序），打開時停在選中的那一列
 * - 傳了 `onExcludedChange` 就開啟「排除」：一個選項是沒選、包含、排除其中一種，
 *   每一列多一顆排除鈕；打開時包含的排最上面、排除的接在後面
 */
export function SelectMenu<T extends string | number = string | number>(props: SelectMenuProps<T>) {
  const { options, icon, label, isLoading, disabled, searchPlaceholder } = props;

  // =====================================================
  // Hooks
  // =====================================================
  const { t } = useTranslation("common");

  /**
   * 是否為手機版（切浮層 / 底部抽屜）
   */
  const isMobile = useIsMobile();

  /**
   * 選單是否開啟
   */
  const [open, setOpen] = useState(false);

  /**
   * 打開當下已選的值；已選的排最上面只照這一份排，勾選過程中清單才不會跳動
   */
  const [openingSelection, setOpeningSelection] = useState<ReadonlySet<string | number>>(
    () => new Set()
  );

  /**
   * 打開當下被排除的值；排在已選的後面，同樣只照這一份排
   */
  const [openingExclusion, setOpeningExclusion] = useState<ReadonlySet<string | number>>(
    () => new Set()
  );

  // =====================================================
  // Variables
  // =====================================================
  /**
   * 可不可以清空：多選一定可以；單選看 `clearable`
   */
  const isClearable = props.multiple === true || props.clearable !== false;

  /**
   * 有沒有開啟排除：有傳 `onExcludedChange` 才開；不可清空的單選一定要有選中的值，所以不提供。
   * 排除不開放，固定為 false（排除鈕、鍵盤的排除、排除計數都不出現）；原本的判斷保留在下面的註解
   */
  // const isExcludable = props.onExcludedChange != null && isClearable;
  const isExcludable: boolean = false;

  /**
   * 目前已選的值（單選也整理成陣列，清單與計數共用同一種形狀）
   */
  const selectedValues = useMemo<(string | number)[]>(() => {
    if (props.multiple) {
      return props.values;
    }

    return props.value !== undefined ? [props.value] : [];
  }, [props.multiple, props.value, props.values]);

  /**
   * 目前被排除的值；沒有開啟排除時一律當成沒有
   */
  const excludedValues = useMemo<(string | number)[]>(
    () => (isExcludable ? (props.excludedValues ?? []) : []),
    [isExcludable, props.excludedValues]
  );

  /**
   * 觸發鈕要不要亮起：有選值或有排除才亮；不可清空的單選永遠有值，亮起沒有意義，所以不亮
   */
  const hasValue = isClearable && (selectedValues.length > 0 || excludedValues.length > 0);

  /**
   * 觸發鈕上的數字：多選的已選數量加上排除數量（單選的選中值直接顯示名稱，不算進數字）
   */
  const filterCount = (props.multiple ? selectedValues.length : 0) + excludedValues.length;

  /**
   * 單選選中的選項名稱
   */
  const selectedLabel = props.multiple
    ? undefined
    : options.find((option) => option.value === props.value)?.label;

  /**
   * 清單的順序：多選打開當下已選的排最上面，被排除的接在後面；
   * 單選只有一個選中的值、已經打勾標出，照原本的順序
   */
  const orderedOptions = useMemo(
    () =>
      props.multiple ? orderSelectedFirst(options, openingSelection, openingExclusion) : options,
    [props.multiple, options, openingSelection, openingExclusion]
  );

  // =====================================================
  // Functions
  // =====================================================
  /**
   * 開關選單；打開時記下當下已選與被排除的值
   */
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setOpeningSelection(new Set(selectedValues));
        setOpeningExclusion(new Set(excludedValues));
      }

      setOpen(nextOpen);
    },
    [selectedValues, excludedValues]
  );

  /**
   * 點選項：多選切換勾選、選單不關；單選選定並關閉（可清空時再點一次選中的會取消）。
   * 這個選項原本被排除（單選是只要有排除）時，排除那一邊有變才一併回報
   */
  const handleSelect = (option: OptionPair) => {
    const nextState = toggleSelectMenuInclusion(
      { values: selectedValues, excludedValues },
      option.value,
      props.multiple === true,
      isClearable
    );

    if (props.multiple) {
      props.onChange(nextState.values as T[]);
    } else {
      props.onChange(nextState.values[0] as T | undefined);
      setOpen(false);
    }

    if (nextState.excludedValues !== excludedValues) {
      props.onExcludedChange?.(nextState.excludedValues as T[]);
    }
  };

  /**
   * 點排除鈕：切換這個選項的排除、選單不關。
   * 這個選項原本被選中（單選是只要有選中的值）時，選中那一邊有變才一併回報
   */
  const handleToggleExclude = (option: OptionPair) => {
    if (!isExcludable) return;

    const nextState = toggleSelectMenuExclusion(
      { values: selectedValues, excludedValues },
      option.value,
      props.multiple === true
    );

    if (nextState.values !== selectedValues) {
      if (props.multiple) {
        props.onChange(nextState.values as T[]);
      } else {
        props.onChange(undefined);
      }
    }

    props.onExcludedChange?.(nextState.excludedValues as T[]);
  };

  /**
   * 清除：已選與排除一起清掉（有值的那一邊才回報）。多選選單不關；單選清掉並關閉
   */
  const handleClear = () => {
    if (selectedValues.length > 0) {
      if (props.multiple) {
        props.onChange([]);
      } else {
        props.onChange(undefined);
      }
    }

    if (excludedValues.length > 0) {
      props.onExcludedChange?.([]);
    }

    if (!props.multiple) {
      setOpen(false);
    }
  };

  // =====================================================
  // Render
  // =====================================================
  /**
   * 觸發鈕：樣式和其他篩選鈕相同（aiFilter）。
   * 多選顯示已選數量；可清空的單選顯示「名稱：選中的值」；不可清空的單選直接顯示選中的值；
   * 有排除時再多一顆排除數量
   */
  const triggerButton = (
    <Button
      variant="aiFilter"
      active={hasValue}
      isLoading={isLoading}
      disabled={disabled}
      aria-haspopup="listbox"
      className="h-10.5 text-dense group gap-2.5 px-3.5 font-medium"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {icon}

        {props.multiple || selectedLabel == null ? (
          <span className="text-nowrap">{label}</span>
        ) : isClearable ? (
          <>
            <span className="text-nowrap">{t("select_menu.label_with_value", { label })}</span>
            <span className="max-w-37.5 -ml-2 truncate">{selectedLabel}</span>
          </>
        ) : (
          <span className="text-nowrap">{selectedLabel}</span>
        )}

        {/* 設了幾個條件：多選的已選數量加上排除數量，有值時滑入（比照其他篩選鈕）。
            包含與排除不分顏色，細節看選單裡面與已套用的條件 */}
        {filterCount > 0 ? (
          <span
            aria-label={
              excludedValues.length > 0
                ? t("select_menu.excluded_count", { count: excludedValues.length })
                : undefined
            }
            className="bg-primary text-primary-foreground animate-in fade-in zoom-in-75 slide-in-from-left-1 h-4.5
              min-w-4.5 grid place-items-center rounded-full px-1 text-[10px] font-bold tabular-nums duration-200"
          >
            {filterCount}
          </span>
        ) : null}
      </div>

      <ChevronDown
        className={cn(
          "text-muted-foreground transition-transform duration-200",
          hasValue && "text-primary",
          open && "rotate-180"
        )}
      />
    </Button>
  );

  /**
   * 清單本體（桌面浮層與手機抽屜共用）
   */
  const panel = (
    <SelectMenuPanel
      options={orderedOptions}
      selectedValues={selectedValues}
      excludedValues={excludedValues}
      label={label}
      isMultiple={props.multiple === true}
      isClearable={isClearable}
      isExcludable={isExcludable}
      isMobile={isMobile}
      searchPlaceholder={searchPlaceholder}
      onSelect={handleSelect}
      onToggleExclude={handleToggleExclude}
      onClear={handleClear}
      onClose={() => setOpen(false)}
    />
  );

  // 手機：底部抽屜（把手、標題、關閉鈕固定在上面，清單在中間捲動）
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent className="max-h-[82vh] rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
          {/* DrawerContent 是橫向的 flex 容器，內層要 flex-1 才會撐滿寬度 */}
          <div className="flex max-h-[82vh] min-h-0 flex-1 flex-col overflow-hidden">
            <div className="bg-border mx-auto mt-2 h-1 w-10 shrink-0 rounded-full" />

            <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-3 pt-3">
              <div className="min-w-0">
                <DrawerTitle className="truncate text-base font-bold">{label}</DrawerTitle>
                <DrawerDescription className="text-muted-foreground-darker mt-0.5 text-xs">
                  {props.multiple ? t("select_menu.hint_multiple") : t("select_menu.hint_single")}
                </DrawerDescription>
              </div>

              <DrawerClose asChild>
                <Button
                  variant="icon"
                  size="icon"
                  className="size-8"
                  aria-label={t("button.close")}
                >
                  <X className="size-4!" />
                </Button>
              </DrawerClose>
            </div>

            {panel}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // 桌面：浮層；有搜尋框時固定寬度，沒有時至少和觸發鈕一樣寬
  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={cn(
          "border-border-lighter max-w-[calc(100vw-1.5rem)] overflow-hidden p-0 shadow-lg",
          options.length > SELECT_MENU_SEARCH_THRESHOLD
            ? "w-75"
            : "w-auto min-w-[max(13.75rem,var(--radix-popover-trigger-width))]"
        )}
        // 焦點由清單自己決定（有搜尋框就放搜尋框），不讓浮層搶走
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {panel}
      </PopoverContent>
    </Popover>
  );
}

interface SelectMenuPanelProps {
  /**
   * 選項（已經排好順序）
   */
  options: OptionPair[];
  /**
   * 目前已選的值
   */
  selectedValues: (string | number)[];
  /**
   * 目前被排除的值
   */
  excludedValues: (string | number)[];
  /**
   * 選單名稱（清單的無障礙名稱）
   */
  label: string;
  /**
   * 是不是多選
   */
  isMultiple: boolean;
  /**
   * 可不可以清空
   */
  isClearable: boolean;
  /**
   * 有沒有開啟排除（每一列出現排除鈕）
   */
  isExcludable: boolean;
  /**
   * 是不是手機版（列高、搜尋框與底部按鈕的樣式不同）
   */
  isMobile: boolean;
  /**
   * 搜尋框的提示文字
   */
  searchPlaceholder?: string;
  /**
   * 點了某個選項
   */
  onSelect: (option: OptionPair) => void;
  /**
   * 點了某個選項的排除鈕
   */
  onToggleExclude: (option: OptionPair) => void;
  /**
   * 點了「清除」
   */
  onClear: () => void;
  /**
   * 點了「完成」
   */
  onClose: () => void;
}

/**
 * 選單的內容：搜尋框、虛擬清單、底部的計數與按鈕
 */
function SelectMenuPanel({
  options,
  selectedValues,
  excludedValues,
  label,
  isMultiple,
  isClearable,
  isExcludable,
  isMobile,
  searchPlaceholder,
  onSelect,
  onToggleExclude,
  onClear,
  onClose,
}: SelectMenuPanelProps) {
  // =====================================================
  // Hooks
  // =====================================================
  const { t } = useTranslation("common");

  /**
   * 整個內容區（沒有搜尋框時把焦點放在這裡，鍵盤才操作得到）
   */
  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * 搜尋框
   */
  const searchInputRef = useRef<HTMLInputElement>(null);

  /**
   * 清單的捲動容器（虛擬捲動要量它的高度）
   */
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * 搜尋的關鍵字
   */
  const [keyword, setKeyword] = useState("");

  /**
   * 鍵盤（或滑鼠移過）停在哪一列；-1＝沒有停在任何一列。
   * 打開時停在第一個選中的選項（單選照原本順序，選中的不一定在最上面），沒有就停在第一個可以選的
   */
  const [activeIndex, setActiveIndex] = useState(() => {
    const selectedIndex = options.findIndex(
      (option) => selectedValues.includes(option.value) && !isSelectMenuOptionDisabled(option)
    );

    return selectedIndex !== -1 ? selectedIndex : findNextEnabledIndex(options, -1, 1);
  });

  // =====================================================
  // Variables
  // =====================================================
  /**
   * 要不要顯示搜尋框：選項夠多才顯示
   */
  const isSearchable = options.length > SELECT_MENU_SEARCH_THRESHOLD;

  /**
   * 符合關鍵字的選項
   */
  const filteredOptions = useMemo(
    () => filterSelectMenuOptions(options, keyword),
    [options, keyword]
  );

  /**
   * 已選的值（查詢用）
   */
  const selectedValueSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  /**
   * 被排除的值（查詢用）
   */
  const excludedValueSet = useMemo(() => new Set(excludedValues), [excludedValues]);

  /**
   * 有沒有任何已選或被排除的值（「清除」能不能按）
   */
  const hasAnyValue = selectedValues.length > 0 || excludedValues.length > 0;

  /**
   * 每一列的高度
   */
  const rowHeight = isMobile ? MOBILE_ROW_HEIGHT : DESKTOP_ROW_HEIGHT;

  /**
   * 虛擬捲動：只畫出看得到的列
   */
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => rowHeight,
    overscan: 6,
  });

  /**
   * 這一幀要畫的列
   */
  const virtualRows = virtualizer.getVirtualItems();

  /**
   * 整份清單的總高度
   */
  const totalHeight = virtualizer.getTotalSize();

  /**
   * 要不要顯示底部那一列：多選一定有；單選只有桌面版、而且可以清空時才有（放「清除」）。
   * 開啟排除的單選點排除鈕不會關閉選單，手機版也要有底部的「清除」與「完成」
   */
  const isFooterVisible = isMultiple || isExcludable || (!isMobile && isClearable);

  /**
   * 底部左邊的計數文字；有排除時後面加上排除數量
   */
  const summaryText = !isMultiple
    ? selectedValues.length > 0
      ? t("select_menu.selected_one")
      : excludedValues.length > 0
        ? t("select_menu.selected_none_with_excluded", { excluded: excludedValues.length })
        : t("select_menu.selected_none")
    : keyword.trim() !== ""
      ? excludedValues.length > 0
        ? t("select_menu.summary_filtered_with_excluded", {
            total: filteredOptions.length.toLocaleString(),
            count: selectedValues.length,
            excluded: excludedValues.length,
          })
        : t("select_menu.summary_filtered", {
            total: filteredOptions.length.toLocaleString(),
            count: selectedValues.length,
          })
      : excludedValues.length > 0
        ? t("select_menu.summary_with_excluded", {
            total: options.length.toLocaleString(),
            count: selectedValues.length,
            excluded: excludedValues.length,
          })
        : t("select_menu.summary", {
            total: options.length.toLocaleString(),
            count: selectedValues.length,
          });

  // =====================================================
  // Functions
  // =====================================================
  /**
   * 改關鍵字：清單回到最上面，停在第一個可以選的選項
   */
  const handleKeywordChange = (nextKeyword: string) => {
    setKeyword(nextKeyword);
    setActiveIndex(findNextEnabledIndex(filterSelectMenuOptions(options, nextKeyword), -1, 1));
    listRef.current?.scrollTo({ top: 0 });
  };

  /**
   * 鍵盤操作：上下鍵移動（跳過不能選的、到頭繞回另一端），Enter 選取停著的那一列；
   * 開啟排除時 Delete／Backspace 切換停著那一列的排除（搜尋框裡有字時留給搜尋框刪字）
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();

      const nextIndex = findNextEnabledIndex(
        filteredOptions,
        activeIndex,
        event.key === "ArrowDown" ? 1 : -1
      );

      if (nextIndex === -1) return;

      setActiveIndex(nextIndex);
      virtualizer.scrollToIndex(nextIndex);
      return;
    }

    if (event.key === "Enter") {
      const activeOption = filteredOptions[activeIndex];

      if (activeOption == null || isSelectMenuOptionDisabled(activeOption)) return;

      event.preventDefault();
      onSelect(activeOption);
      return;
    }

    if (isExcludable && (event.key === "Delete" || event.key === "Backspace")) {
      if (event.target === searchInputRef.current && keyword !== "") return;

      const activeOption = filteredOptions[activeIndex];

      if (activeOption == null || isSelectMenuOptionDisabled(activeOption)) return;

      event.preventDefault();
      onToggleExclude(activeOption);
    }
  };

  /**
   * 桌面打開時把焦點放進來：有搜尋框放搜尋框，沒有就放整個內容區。
   * 手機不自動放進搜尋框，避免一打開鍵盤就把清單蓋住
   */
  useEffect(() => {
    if (isMobile) return;

    (searchInputRef.current ?? rootRef.current)?.focus();
  }, [isMobile]);

  /**
   * 打開時捲到停著的那一列（單選選中的在清單下面時才看得到）；只在打開時做一次
   */
  useEffect(() => {
    if (activeIndex > 0) virtualizer.scrollToIndex(activeIndex);
    // 只在打開時捲一次，之後由鍵盤操作自己捲
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =====================================================
  // Render
  // =====================================================
  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className="outline-hidden flex min-h-0 flex-1 flex-col"
      onKeyDown={handleKeyDown}
    >
      {/* 搜尋框 */}
      {isSearchable ? (
        <div
          className={cn(
            "flex shrink-0 items-center gap-2",
            isMobile
              ? "bg-muted border-border-lighter mx-4 mb-2 rounded-xl border px-3"
              : "border-border-lighter border-b px-3"
          )}
        >
          <Search className="text-muted-foreground size-4 shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            autoComplete="off"
            spellCheck={false}
            aria-label={searchPlaceholder ?? t("select_menu.search_placeholder")}
            placeholder={searchPlaceholder ?? t("select_menu.search_placeholder")}
            // 手機用 16px 的字，iOS 才不會在點進輸入框時自動放大畫面
            className={cn(
              "placeholder:text-muted-foreground outline-hidden min-w-0 flex-1 bg-transparent",
              isMobile ? "h-11 text-base" : "h-10 text-sm"
            )}
            value={keyword}
            onChange={(event) => handleKeywordChange(event.target.value)}
          />
          {keyword !== "" ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground rounded-md p-1"
              aria-label={t("select_menu.clear_search")}
              onClick={() => {
                handleKeywordChange("");
                searchInputRef.current?.focus();
              }}
            >
              <X className="size-3.5!" />
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* 清單 */}
      {filteredOptions.length > 0 ? (
        <div
          ref={listRef}
          role="listbox"
          aria-label={label}
          aria-multiselectable={isMultiple}
          className={cn(
            "overflow-y-auto overscroll-contain",
            isMobile ? "min-h-0 flex-1 px-2" : "p-1"
          )}
          style={
            isMobile
              ? undefined
              : { height: Math.min(totalHeight + LIST_VERTICAL_PADDING, DESKTOP_LIST_MAX_HEIGHT) }
          }
        >
          <div className="relative w-full" style={{ height: totalHeight }}>
            {virtualRows.map((virtualRow) => {
              const option = filteredOptions[virtualRow.index];
              const isSelected = selectedValueSet.has(option.value);
              const isExcluded = excludedValueSet.has(option.value);
              const isDisabled = isSelectMenuOptionDisabled(option);
              const isActive = virtualRow.index === activeIndex;
              const labelParts = splitLabelByKeyword(option.label, keyword);

              return (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  data-active={isActive}
                  data-excluded={isExcluded}
                  className={cn(
                    "absolute left-0 top-0 flex w-full cursor-pointer select-none items-center gap-2",
                    isMobile ? "rounded-xl px-3 text-[15px]" : "rounded-lg px-2.5 text-sm",
                    isSelected && "text-primary font-medium",
                    isExcluded && "text-muted-foreground-darker",
                    isActive && (isSelected ? "bg-primary-50" : "bg-accent"),
                    isDisabled && "cursor-not-allowed opacity-45"
                  )}
                  style={{
                    height: virtualRow.size,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  onClick={() => {
                    if (isDisabled) return;

                    setActiveIndex(virtualRow.index);
                    onSelect(option);
                  }}
                  onMouseMove={() => {
                    if (isMobile || isDisabled || isActive) return;

                    setActiveIndex(virtualRow.index);
                  }}
                >
                  {option.leadingNode}

                  <span className={cn("min-w-0 flex-1 truncate", isExcluded && "line-through")}>
                    {option.labelNode ?? (
                      <>
                        {labelParts.before}
                        {labelParts.match !== "" ? (
                          <mark className="text-primary bg-transparent font-bold">
                            {labelParts.match}
                          </mark>
                        ) : null}
                        {labelParts.after}
                      </>
                    )}
                  </span>

                  {option.trailingNode}

                  {(option.disabledReason ?? option.disabledTooltip) != null && isDisabled ? (
                    <Badge variant="outline" className="shrink-0">
                      {option.disabledReason ?? option.disabledTooltip}
                    </Badge>
                  ) : null}

                  {/* 排除鈕：高度小於列高，不會把列撐高。平常是低調的灰字，停在這一列（或手機版）才明顯；
                      已排除時固定顯示。不進 Tab 順序（列本身也不是），鍵盤用 Delete／Backspace */}
                  {/* 選中的打勾（多選也是打勾，不用核取方塊）；被排除的列不打勾，靠刪除線與「已排除」辨識。
                      放在排除鈕的左邊，排除鈕才會貼齊列的右緣 */}
                  <Check
                    strokeWidth={2.6}
                    className={cn(
                      "text-primary shrink-0 transition-[opacity,transform] duration-100",
                      isMobile ? "size-5" : "size-4.5",
                      isSelected ? "opacity-100" : "scale-50 opacity-0"
                    )}
                  />
                  {isExcludable ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      tabIndex={-1}
                      disabled={isDisabled}
                      aria-pressed={isExcluded}
                      // 只有圖示，名稱給讀屏與滑過的提示用
                      aria-label={isExcluded ? t("select_menu.excluded") : t("select_menu.exclude")}
                      title={isExcluded ? t("select_menu.excluded") : t("select_menu.exclude")}
                      className={cn(
                        "shrink-0 rounded-md border border-transparent p-0",
                        isMobile ? "size-8" : "size-6.5",
                        isExcluded
                          ? "bg-muted text-muted-foreground-darker border-border"
                          : isMobile || isActive
                            ? "border-border bg-on-background text-foreground"
                            : "text-muted-foreground"
                      )}
                      onClick={(event) => {
                        // 不讓點擊傳到列本身，否則會同時觸發選取
                        event.stopPropagation();
                        setActiveIndex(virtualRow.index);
                        onToggleExclude(option);
                      }}
                    >
                      <CircleMinus className="size-4!" />
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-muted-foreground-darker px-3 py-7 text-center text-sm">
          {t("select_menu.empty")}
        </div>
      )}

      {/* 底部：桌面是計數＋清除；手機是清除＋完成兩顆大按鈕 */}
      {isFooterVisible ? (
        isMobile ? (
          <div className="border-border-lighter flex shrink-0 gap-3 border-t px-4 py-3">
            <Button
              type="button"
              variant="outline"
              className="h-11 flex-1"
              disabled={!hasAnyValue}
              onClick={onClear}
            >
              {t("select_menu.clear")}
            </Button>
            <Button type="button" variant="default" className="h-11 flex-1" onClick={onClose}>
              {selectedValues.length > 0
                ? t("select_menu.done_with_count", { count: selectedValues.length })
                : t("select_menu.done")}
            </Button>
          </div>
        ) : (
          <div
            className="border-border-lighter text-muted-foreground-darker flex shrink-0 items-center justify-between gap-2
              border-t py-2 pl-3 pr-2.5 text-xs tabular-nums"
          >
            <span>{summaryText}</span>
            <Button
              type="button"
              variant="ghostPrimary"
              size="xs"
              className="px-2"
              disabled={!hasAnyValue}
              onClick={onClear}
            >
              {t("select_menu.clear")}
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}
